import cn from 'classnames'
import { Decoration } from 'prosemirror-view'

import {
  HeaderOrFooterPrefs,
  SlotConfig,
} from '#util/zodSchemas/printPreferences'

/** Per-page data used to render a header/footer margin widget. */
export interface PageMarginPageContext {
  pageNumber: number
  pageAlias?: string
  isTitlePage?: boolean
  collatedRevision?: string
  pageSection?: string
  sceneElementNumber?: string
  sceneContdHeaderText?: string
  sceneContdFooterText?: string
  dialogueContdText?: string
  dialogueMoreText?: string
}

/** Script-wide data shared by every page's header/footer widget. */
export interface PageMarginScriptContext {
  title?: string
  timestamp?: string
  date?: string
  duration?: string
  activeRevisionLabel?: string
  totalPages?: number
}

/** Combined context passed to the header/footer widget builders. */
export interface PageMarginContext {
  script: PageMarginScriptContext
  headers: HeaderOrFooterPrefs
  footers: HeaderOrFooterPrefs
}

/** Builds the running header decoration for one physical page. */
export function buildPageHeaderElement(
  pos: number,
  page: PageMarginPageContext,
  ctx: PageMarginContext,
): Decoration {
  const wrapper = document.createElement('div')
  wrapper.className = 'print-page-header'
  const inset = buildInsetWrapper(wrapper)

  if (isSlotAreaVisible(page, ctx.headers)) {
    inset.appendChild(buildSlotRow('header', ctx.headers, page, ctx.script))
    inset.appendChild(buildDivider('header', ctx.headers.divider))
  }
  // Always render both lines, even when empty, so the header reserves the
  // same height on every page regardless of whether this page happens to
  // carry a scene-contd/dialogue-contd marker.
  inset.appendChild(
    buildSceneContdHeaderLine(
      page.sceneContdHeaderText ?? PLACEHOLDER_LINE_TEXT,
      page.sceneContdHeaderText ? page.sceneElementNumber : undefined,
    ),
  )
  appendMarginLine(
    inset,
    'print-page-header',
    page.dialogueContdText ?? PLACEHOLDER_LINE_TEXT,
    'print-page-dialogue-line',
  )

  return Decoration.widget(pos, () => wrapper, { side: -1 })
}

/** Builds the running footer decoration for one physical page. */
export function buildPageFooterElement(
  pos: number,
  page: PageMarginPageContext,
  ctx: PageMarginContext,
): Decoration {
  const wrapper = document.createElement('div')
  wrapper.className = 'print-page-footer'
  const inset = buildInsetWrapper(wrapper)

  // Always render both lines — see the matching comment in
  // buildPageHeaderElement for why.
  appendMarginLine(
    inset,
    'print-page-footer',
    page.dialogueMoreText ?? PLACEHOLDER_LINE_TEXT,
    'print-page-dialogue-line',
  )
  appendMarginLine(
    inset,
    'print-page-footer',
    page.sceneContdFooterText ?? PLACEHOLDER_LINE_TEXT,
    'print-page-scene-contd-line',
  )
  if (isSlotAreaVisible(page, ctx.footers)) {
    inset.appendChild(buildDivider('footer', ctx.footers.divider))
    inset.appendChild(buildSlotRow('footer', ctx.footers, page, ctx.script))
  }

  return Decoration.widget(pos, () => wrapper, { side: 1 })
}

/** Non-breaking space: a plain space here collapses to zero height in Prince. */
export const PLACEHOLDER_LINE_TEXT = ' '

// Prince ignores margin on a running() element (it's out of normal flow),
// so a non-running wrapper establishes the containing block the
// absolutely-positioned inset anchors to.
function buildInsetWrapper(runningRoot: HTMLElement): HTMLElement {
  const relativeWrapper = document.createElement('div')
  relativeWrapper.className = 'print-page-relative-wrapper'
  runningRoot.appendChild(relativeWrapper)

  const inset = document.createElement('div')
  inset.className = 'print-page-inset'
  relativeWrapper.appendChild(inset)

  return inset
}

function isSlotAreaVisible(
  page: PageMarginPageContext,
  prefs?: HeaderOrFooterPrefs,
): boolean {
  if (!prefs || prefs.enabled === false) return false
  if (page.pageNumber === 1 && prefs.showOnFirstPage !== true) return false
  return true
}

function buildDivider(
  position: 'header' | 'footer',
  visible: boolean | undefined,
): HTMLElement {
  const divider = document.createElement('hr')
  divider.className = cn(
    'print-page-divider',
    `print-page-divider-${position}`,
    {
      'print-page-divider-hidden': !visible,
    },
  )
  return divider
}

function buildSlotRow(
  position: 'header' | 'footer',
  prefs: HeaderOrFooterPrefs,
  page: PageMarginPageContext,
  script: PageMarginScriptContext,
): HTMLElement {
  const grouping = document.createElement('div')
  grouping.className = cn(
    'print-page-slot-row',
    `print-page-slot-row-${position}`,
    { 'print-page-has-center': !!prefs.slots.center },
  )

  grouping.appendChild(buildSlot('left', prefs.slots.left, page, script))
  grouping.appendChild(buildSlot('center', prefs.slots.center, page, script))
  grouping.appendChild(buildSlot('right', prefs.slots.right, page, script))

  return grouping
}

function buildSlot(
  position: 'left' | 'center' | 'right',
  config: SlotConfig | undefined,
  page: PageMarginPageContext,
  script: PageMarginScriptContext,
): HTMLElement {
  const wrapper = document.createElement('div')
  wrapper.className = cn('print-page-slot', `print-page-slot-${position}`, {
    'print-page-slot-circled': config?.decoration?.circled,
    'print-page-slot-large': config?.decoration?.large,
  })
  wrapper.textContent = renderSlotText(config, page, script)

  return wrapper
}

function renderSlotText(
  config: SlotConfig | undefined,
  page: PageMarginPageContext,
  script: PageMarginScriptContext,
): string {
  if (!config) return ''
  if (config.slotType === 'literal') return config.text
  // rundown cell values aren't wired up for script printing yet
  if (config.slotType === 'rundown-cell-value') return ''

  switch (config.contentType) {
    case 'revisions':
      return script.activeRevisionLabel ?? ''
    case 'revisions-collated':
      return page.isTitlePage ? '' : (page.collatedRevision ?? '')
    case 'page-number':
      return page.isTitlePage ? '' : `${page.pageAlias ?? page.pageNumber}.`
    case 'page-number-total':
      return page.isTitlePage
        ? ''
        : `Page ${page.pageNumber}/${script.totalPages ?? ''}`
    case 'short-title':
      return script.title ?? ''
    case 'full-title':
      return [script.title, page.pageSection?.toUpperCase()]
        .filter(Boolean)
        .join('\n')
    case 'timestamp':
      return script.timestamp ?? ''
    case 'date':
      return script.date ?? ''
    case 'duration':
      return script.duration ?? ''
    default:
      return ''
  }
}

function appendMarginLine(
  wrapper: HTMLElement,
  className: string,
  text: string | undefined,
  extraClassName?: string,
) {
  if (!text) return
  const lineEl = document.createElement('div')
  lineEl.className = [`${className}-line`, extraClassName]
    .filter(Boolean)
    .join(' ')
  lineEl.textContent = text
  wrapper.appendChild(lineEl)
}

// Matches editor's buildSceneContdHeader (page-decoration/widgets.ts)
// exactly: prefix text plus a scene-element-number flanking each side of
// the line.
function buildSceneContdHeaderLine(
  text: string,
  sceneElementNumber: string | undefined,
): HTMLElement {
  const lineEl = document.createElement('div')
  lineEl.className = 'print-page-header-line print-page-scene-contd-line'
  lineEl.appendChild(document.createTextNode(text))

  if (sceneElementNumber) {
    const left = document.createElement('span')
    left.className = 'print-page-scene-contd-left-number'
    left.textContent = sceneElementNumber
    lineEl.appendChild(left)

    const right = document.createElement('span')
    right.className = 'print-page-scene-contd-right-number'
    right.textContent = sceneElementNumber
    lineEl.appendChild(right)
  }

  return lineEl
}
